import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Skeleton as MuiSkeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AxiosError } from 'axios';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import z from 'zod';
import { NextStepsDialog } from '@/packages/courses/pages/registration/components/next-steps-dialog';
import { EnrollmentEvent } from '@/constants/analytics';
import { useNotification } from '@/context/notification';
import { useCourseCoupon } from '@/data/catalog/coupon';
import { useCatalogOffer } from '@/data/catalog/offer';
import { useCreateRegistration } from '@/data/courses/registration';
import { InstitutionLogo } from '@/packages/courses/components/institution-logo';
import { Prices } from '@/packages/courses/components/prices';
import type { RegistrationFormData } from '@/packages/courses/components/user-data-form/schema';
import {
  buildOfferEnrollmentProperties,
  getRegistrationNextStep,
} from '@/packages/courses/utils/enrollment-analytics';
import { handleSubscriptionError } from '@/packages/courses/utils/error-message-handlers';
import { track } from '@/utils/analytics';
import { captureException } from '@/utils/capture-exception';
import { extractNumbers } from '@/utils/extract-numbers';

const couponSchema = z.object({
  coupon: z.string().min(1, 'Campo obrigatório'),
});

type CouponFormData = z.infer<typeof couponSchema>;

const Skeleton = () => (
  <>
    <MuiSkeleton variant="rounded" width="100%" height={70} />
    <MuiSkeleton variant="rounded" width="100%" height={70} />
    <MuiSkeleton variant="rounded" width="100%" height={70} />
  </>
);

export const OfferCard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { offer, isLoading } = useCatalogOffer(Number(id));
  const { validateCoupon, isPending } = useCourseCoupon();
  const { mutate: createRegistration, isPending: isCreating } =
    useCreateRegistration();
  const { handleSubmit } = useFormContext<RegistrationFormData>();
  const { toast } = useNotification();

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedPhone, setSubmittedPhone] = useState('');

  const {
    control,
    formState,
    setValue,
    setError,
    clearErrors,
    handleSubmit: handleCouponSubmit,
  } = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      coupon: '',
    },
  });

  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);

  const enrollmentProperties = buildOfferEnrollmentProperties(offer);

  const handleApplyCoupon = async (data: CouponFormData) => {
    const coupon = data.coupon.toUpperCase();

    validateCoupon(
      { coupon: data.coupon, courseId: Number(id) },
      {
        onSuccess: () => {
          track(EnrollmentEvent.RegistrationCouponApplied, {
            ...enrollmentProperties,
            success: true,
            coupon,
          });
          setAppliedCoupon(coupon);
          clearErrors();
        },
        onError: () => {
          track(EnrollmentEvent.RegistrationCouponApplied, {
            ...enrollmentProperties,
            success: false,
            coupon,
          });
          setError('coupon', { message: 'Cupom inválido' });
        },
      }
    );
  };

  const onSubmit = (data: RegistrationFormData) => {
    const [day, month, year] = data.birthDate.split('/');
    const birthDateISO = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    const addressNumber = data.no_number ? 'S/N' : data.number || '';

    const payload = {
      nome: data.name,
      cpf: extractNumbers(data.cpf),
      birth_date: birthDateISO,
      country_code: data.countryCode,
      telefone: extractNumbers(data.phone),
      email: data.email,
      full_address: {
        postal_code: extractNumbers(data.cep),
        city: data.city,
        state: data.state,
        street: data.street,
        neighborhood: data.neighborhood,
        number: addressNumber,
        complement: data.complement ?? '',
      },
      address: `${data.street} ${addressNumber}${
        data.complement ? ` - ${data.complement}` : ''
      }, ${data.neighborhood}, ${data.cep} - ${data.city}, ${data.state}`,
      cupom: appliedCoupon ?? '',
    };

    createRegistration(
      { courseId: Number(id), body: payload },
      {
        onSuccess: () => {
          track(EnrollmentEvent.RegistrationSubmitted, {
            ...enrollmentProperties,
            success: true,
            has_coupon: Boolean(appliedCoupon),
            next_step: getRegistrationNextStep(
              offer?.institution_subscription_config
            ),
          });
          setSubmittedEmail(data.email);
          setSubmittedPhone(data.phone);
          setShowSuccessDialog(true);
        },
        onError: (error: Error) => {
          const axiosError = error as AxiosError<{ data: { message: string } }>;

          const { errorMessage, isUnknownError } =
            handleSubscriptionError(axiosError);

          if (isUnknownError) captureException({ error, sensitive: true });

          track(EnrollmentEvent.RegistrationSubmitted, {
            ...enrollmentProperties,
            success: false,
            has_coupon: Boolean(appliedCoupon),
            error_reason: errorMessage,
          });

          toast({
            title: 'Erro ao submeter inscrição',
            content: errorMessage,
            type: 'error',
          });
        },
      }
    );
  };

  const handleRemoveCoupon = () => {
    setValue('coupon', '');
    setAppliedCoupon(null);
    clearErrors();
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    navigate('/');
  };

  const showCouponField = offer?.discount_percentage !== 100;

  return (
    <Box
      sx={({ shape }) => ({
        backgroundColor: 'neutral.0',
        borderRadius: `${shape.borderRadius}px`,
        padding: '1px',
      })}
      width="100%"
      maxWidth={{ xs: '100%', md: 400 }}
    >
      {offer?.institution && (
        <InstitutionLogo
          institution={offer.institution}
          isLoading={isLoading}
        />
      )}
      <Stack bgcolor="white" p={3} spacing={2}>
        {offer?.pricing_description && (
          <Prices
            isFullIncluded={offer?.is_full_included ?? false}
            pricing={offer?.pricing_description}
            isOfferFiltersLoading={isLoading}
            isSingleOffer={offer?.offers_quantity === 1}
          />
        )}
        <Divider />
        {isLoading && <Skeleton />}
        {!isLoading && (
          <>
            <Box>
              <Typography fontWeight="semibold">Localização</Typography>
              <Typography>{offer?.campus?.state ?? ''}</Typography>
              <Typography>{offer?.campus?.city ?? ''}</Typography>
            </Box>
            <Box>
              <Typography fontWeight="semibold">Polo</Typography>
              <Typography>{offer?.course?.modality?.label ?? ''}</Typography>
            </Box>
            <Box>
              <Typography fontWeight="semibold">Turno</Typography>
              <Typography>{offer?.period?.label ?? ''}</Typography>
            </Box>
            {showCouponField && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  {appliedCoupon && (
                    <Stack
                      px={2}
                      py={1.2}
                      flex={1}
                      spacing={1}
                      direction="row"
                      bgcolor="success.light"
                      borderRadius={({ shape }) => `${shape.borderRadius}px`}
                    >
                      <Typography color="success.dark" variant="h3">
                        Cupom: {appliedCoupon}
                      </Typography>
                      <CheckCircleIcon
                        sx={{ color: 'success.dark', fontSize: '16px' }}
                      />
                    </Stack>
                  )}
                  {!appliedCoupon && (
                    <Controller
                      name="coupon"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Cupom"
                          sx={{ flex: 1 }}
                          error={!!formState.errors.coupon}
                          helperText={formState.errors.coupon?.message}
                          slotProps={{
                            input: {
                              endAdornment: formState.errors.coupon ? (
                                <InputAdornment position="end">
                                  <ErrorOutlineIcon color="error" />
                                </InputAdornment>
                              ) : null,
                            },
                          }}
                        />
                      )}
                    />
                  )}
                  <Button
                    variant={appliedCoupon ? 'text' : 'outlined'}
                    onClick={() =>
                      appliedCoupon
                        ? handleRemoveCoupon()
                        : handleCouponSubmit(handleApplyCoupon)()
                    }
                    loading={isPending}
                    aria-label={
                      appliedCoupon ? 'Remover cupom' : 'Aplicar cupom'
                    }
                  >
                    {!isPending && (appliedCoupon ? 'Remover' : 'Aplicar')}
                  </Button>
                </Stack>
              </Stack>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit(onSubmit)}
              loading={isCreating}
            >
              Enviar informações
            </Button>
          </>
        )}
      </Stack>
      {showSuccessDialog && (
        <NextStepsDialog
          onClose={handleCloseSuccessDialog}
          onExternalLinkClick={() =>
            track(
              EnrollmentEvent.RegistrationExternalLinkClicked,
              enrollmentProperties
            )
          }
          config={offer?.institution_subscription_config}
          institutionName={offer?.institution?.name}
          email={submittedEmail}
          phone={submittedPhone}
        />
      )}
    </Box>
  );
};
